/*
 * Shopify standard storefront cart-event bridge for Velven's AJAX cart paths.
 * The cart mutation remains authoritative; event/readback failures never turn a
 * successful add into a customer-facing error.
 */
(function (global) {
  "use strict";

  if (global.VelvenCartEvents) return;

  // The module import lives inline in theme.liquid. Shopify transpiles theme
  // .js assets and would otherwise rewrite import() to browser-invalid require().
  var eventsReady = global.__velvenStandardEventsReady || Promise.resolve(null);

  function cartUrl() {
    var routes = (global.theme && global.theme.routes) || {};
    return routes.cart_url ? routes.cart_url.replace(/\/$/, "") + ".js" : "/cart.js";
  }

  function linesFor(items) {
    return (items || []).map(function (item) {
      return {
        merchandiseId: item.id,
        quantity: parseInt(item.quantity, 10) || 1
      };
    });
  }

  function trackingItems(result, requestedItems) {
    var returned = result && Array.isArray(result.items)
      ? result.items
      : (result && result.id ? [result] : []);
    var requested = requestedItems || [];
    return requested.map(function (requestItem) {
      var item = returned.find(function (line) {
        return String(line.variant_id || line.id) === String(requestItem.id);
      }) || requestItem;
      var priceCents = item.final_price != null ? item.final_price : item.price;
      var presentmentPrice = item.presentment_price == null ? null : Number(item.presentment_price);
      if (!Number.isFinite(presentmentPrice)) presentmentPrice = null;
      return {
        variant_id: String(item.variant_id || item.id || requestItem.id || ""),
        product_id: String(item.product_id || ""),
        sku: String(item.sku || ""),
        product_title: String(item.product_title || item.title || ""),
        variant_title: String(item.variant_title || ""),
        quantity: parseInt(requestItem.quantity, 10) || 1,
        // Shopify's add response exposes presentment_price in currency units.
        // Prefer it so a response normalized by the storefront cannot be
        // divided by 100 twice; fall back to the documented cent fields.
        price: presentmentPrice != null
          ? presentmentPrice
          : (priceCents == null ? null : Number(priceCents) / 100),
        url: String(item.url || "")
      };
    }).filter(function (item) { return item.variant_id; });
  }

  function publishTrackingFallback(options, result) {
    var items = trackingItems(result, options.items);
    if (!items.length) return;
    if (typeof global.CustomEvent === 'function') {
      global.document.dispatchEvent(new global.CustomEvent('velven:cart-success', { detail: { items: items } }));
    }
    var payload = {
      version: 1,
      queue_id: "atc-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10),
      created_at: Date.now(),
      context: options.context || "product",
      currency: String((global.Shopify && global.Shopify.currency && global.Shopify.currency.active) || "AUD").toUpperCase(),
      items: items
    };

    var privacy = global.Shopify && global.Shopify.customerPrivacy;
    var analyticsAllowed = !!privacy &&
      typeof privacy.analyticsProcessingAllowed === "function" &&
      privacy.analyticsProcessingAllowed();

    // Hand one neutral ecommerce signal to the page integration. Do not call
    // gtag here: freshly compiled themes otherwise process the manual GA4 event
    // and then re-emit a second normalized add with a different identity. Live
    // verification decides whether the integration owns one normalized event.
    if (analyticsAllowed) {
      global.dataLayer = global.dataLayer || [];
      var value = payload.items.reduce(function (sum, item) {
        return sum + ((Number(item.price) || 0) * item.quantity);
      }, 0);
      var gaItems = payload.items.map(function (item) {
        return {
          item_id: item.sku || item.variant_id,
          item_name: item.product_title,
          item_variant: item.variant_title,
          price: Number(item.price) || 0,
          quantity: item.quantity
        };
      });
      var ecommerceSignal = {
        event: "add_to_cart",
        ecommerce: {
          currency: payload.currency,
          value: value,
          items: gaItems
        }
      };
      global.dataLayer.push({ ecommerce: null });
      global.dataLayer.push(ecommerceSignal);
    }

    // Keep the custom event available to consent-managed Shopify pixels. No
    // connected Velven pixel maps this custom event to GA4.
    var analytics = global.Shopify && global.Shopify.analytics;
    if (!analytics || typeof analytics.publish !== "function") return;
    try {
      var published = analytics.publish("velven:add_to_cart", payload);
      if (published && typeof published.catch === "function") published.catch(function () {});
    } catch (error) {
      // Measurement must never turn a successful cart mutation into an error.
    }
  }

  function requestWithTracking(options, request) {
    return Promise.resolve().then(request).then(function (result) {
      publishTrackingFallback(options, result);
      return result;
    });
  }

  function notifyAdd(pending) {
    if (typeof global.CustomEvent === "function") {
      global.document.dispatchEvent(new global.CustomEvent("velven:cart-add", { detail: { pending: pending } }));
    }
  }

  function add(options, request) {
    options = options || {};
    // Preorder rolls get their line item property here, before any request body
    // is built from options.items (assets/vmcm-preorder.js).
    if (global.VelvenPreorder) global.VelvenPreorder.annotate(options.items);
    var target = options.target && typeof options.target.dispatchEvent === "function"
      ? options.target
      : global.document;

    notifyAdd(true);
    return eventsReady.then(function (SE) {
      if (!SE || !SE.CartLinesUpdateEvent || !SE.CartErrorEvent) {
        return requestWithTracking(options, request);
      }
      var deferred = SE.CartLinesUpdateEvent.createPromise();
      // A rejected event promise is useful to listeners but must not surface as
      // an unhandled page error when no app currently subscribes to it.
      deferred.promise.catch(function () {});

      target.dispatchEvent(new SE.CartLinesUpdateEvent({
        action: "add",
        context: options.context || "product",
        lines: linesFor(options.items),
        promise: deferred.promise
      }));

      return requestWithTracking(options, request).then(function (result) {
        // Shopify's event contract resolves with the complete, post-mutation
        // cart. This readback is telemetry only and cannot undo a successful add.
        global.fetch(cartUrl(), { headers: { "Accept": "application/json" } })
          .then(function (response) {
            if (!response.ok) throw new Error("Cart readback failed");
            return response.json();
          })
          .then(function (ajaxCart) {
            deferred.resolve({
              cart: SE.CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart)
            });
          })
          .catch(function (error) { deferred.reject(error); });
        return result;
      }, function (error) {
        target.dispatchEvent(new SE.CartErrorEvent({
          error: error && error.message ? error.message : "Add to cart failed",
          code: "SERVICE_UNAVAILABLE"
        }));
        deferred.reject(error);
        throw error;
      });
    }, function () {
      // Shopify's CDN being unavailable must never block a sale.
      return requestWithTracking(options, request);
    }).then(function (result) {
      notifyAdd(false);
      return result;
    }, function (error) {
      notifyAdd(false);
      throw error;
    });
  }

  global.VelvenCartEvents = {
    add: add,
    ready: eventsReady
  };
})(window);
